import { useEffect, useState } from 'react';

/**
 * MoodMate motion control.
 *
 * Why this exists: honoring the OS `prefers-reduced-motion` with a media query
 * meant that on any machine with system animations turned off (common on managed
 * Windows/corporate images) EVERY MoodMate animation was disabled and the whole
 * feature rendered static — however much animation we added. So motion is driven
 * by an in-app preference instead, exposed as a `data-mm-motion` attribute on
 * <html> that the stylesheets gate on:
 *
 *   data-mm-motion="full"   → animations play (the default — you see motion at once)
 *   data-mm-motion="reduce" → the reduce rules apply and motion is removed
 *
 * The preference is the user's own choice:
 *   'full'   (default) — always animate (gentle, slow, non-vestibular motion)
 *   'reduce'           — minimise motion
 *   'system'           — follow the OS prefers-reduced-motion setting
 *
 * This keeps motion visible by default while still giving an explicit, respectful
 * way to turn it down.
 */

export type MotionPref = 'full' | 'reduce' | 'system';

const KEY = 'creyente:moodmate:motion';
const EVT = 'mm-motion-change';

export const getMotionPref = (): MotionPref => {
  try {
    const v = localStorage.getItem(KEY);
    return v === 'reduce' || v === 'system' ? v : 'full';
  } catch {
    return 'full';
  }
};

const systemReduced = (): boolean => {
  try {
    return !!window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
};

/** Resolve a preference to the concrete mode the CSS keys on. */
export const resolveMotion = (pref: MotionPref = getMotionPref()): 'full' | 'reduce' =>
  pref === 'reduce' ? 'reduce' : pref === 'system' && systemReduced() ? 'reduce' : 'full';

/** Write the resolved mode onto <html> so the stylesheets apply it. */
export const applyMotionPref = (): void => {
  if (typeof document === 'undefined') return;
  document.documentElement.setAttribute('data-mm-motion', resolveMotion());
};

export const setMotionPref = (pref: MotionPref): void => {
  try {
    localStorage.setItem(KEY, pref);
  } catch {
    /* ignore */
  }
  applyMotionPref();
  try {
    window.dispatchEvent(new CustomEvent(EVT));
  } catch {
    /* ignore */
  }
};

// Apply as soon as this module loads, before the first MoodMate paint.
applyMotionPref();

/** True when motion is currently reduced (by preference or, for 'system', the OS). */
export const useMotionReduced = (): boolean => {
  const [reduced, setReduced] = useState<boolean>(() => resolveMotion() === 'reduce');
  useEffect(() => {
    const update = () => setReduced(resolveMotion() === 'reduce');
    window.addEventListener(EVT, update);
    const mq = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    mq?.addEventListener?.('change', update);
    return () => {
      window.removeEventListener(EVT, update);
      mq?.removeEventListener?.('change', update);
    };
  }, []);
  return reduced;
};
